/**
 * The contract between the in-page playground runtime and the node-side runner that drives it.
 *
 * Both halves import THIS module so a key can only ever be spelled once. Nothing here has behaviour;
 * it is the vocabulary the two sides agree on.
 */

/** `window` globals the runtime publishes and the runner reads (or the runner injects and the runtime reads). */
export const PLAYGROUND_GLOBALS = {
  /** Re-entrancy guard: `bootstrapPlayground` has STARTED. Says nothing about readiness. */
  bootstrapped: '__GOTTI_PLAYGROUND_BOOTSTRAPPED__',
  /**
   * The environment is up and the page can be driven. Set after the setup phase resolves and BEFORE
   * any test runs, so a serve budget measures serve time and never test time.
   */
  ready: '__GOTTI_PLAYGROUND_READY__',
  /** Injected by the runner before navigation: run the test suite once bootstrapped. */
  runTests: '__GOTTI_PLAYGROUND_RUN_TESTS__',
  /** Injected by the runner: run only the test with this label. */
  testLabel: '__GOTTI_PLAYGROUND_TEST_LABEL__',
  /** Published by the runtime: the suite result; `complete: true` once every test has finished. */
  testResult: '__GOTTI_PLAYGROUND_TEST_RESULT__',
  /** Runtime-internal: set while a triggered suite run is in progress, so a second trigger in the same page skips. */
  testRunning: '__GOTTI_PLAYGROUND_TEST_RUNNING__',
  /**
   * Published by the runtime by READY: a function returning the canvas each of the playground's clients draws on (its
   * `envOptions.canvas`), once each — every client test-utils created in the page while the boot ran, from its start until
   * its suite finished (in a process that boots one playground after another, a client made while two boots overlap is
   * recorded by neither), in creation order (whoever created it: the default setup, a `setup` that keeps its environment
   * to itself, a step after READY), then any client of the environment it did not see created. Read each time it is called; empty when no client has a canvas. `--video`'s frame gate asks it, on every
   * poll while its render anchor waits, whether the world is on screen at all (`frame-gate.ts`): a canvas created after
   * the anchor resolved is listed but never judged.
   */
  clientCanvases: '__GOTTI_PLAYGROUND_CLIENT_CANVASES__',
  /**
   * Published by the runtime: the page handle of the playground booted in this page — a `PlaygroundPageHandle`, whose
   * `env`, `clients`, `client()`, `areas` and `game` are what the playground's step contexts are built from. It is the
   * supported way for a script outside the page (a Playwright recorder) to reach the playground's environment, in place
   * of a global a definition sets itself: `window.__GOTTI_PLAYGROUND_ENVIRONMENT__.env.system(...)`.
   *
   * Set the moment the boot completes — `setup` (or the default setup) built the environment, the game hooks are
   * applied — so before READY under `bootstrapPlayground` (a served page, and a `--test` run's), and before
   * `mountPlayground` resolves. Its `game` is `undefined` until a suite run's `game` builder resolves (before that run's
   * first test); a run that starts resets it. Deleted when a mount disposes; replaced by the next boot in the page (a
   * mount after that dispose). A handle kept past its playground's dispose, or past its replacement, throws on every read.
   * Under the dashboard (`--ui`) it is the `window` of the dashboard's preview iframe (`PREVIEW_IFRAME_SELECTOR`), where
   * the playground runs — never the top page's, which holds the dashboard.
   */
  environment: '__GOTTI_PLAYGROUND_ENVIRONMENT__',
  /**
   * Set by the node-side runner on the document under test once it has accepted READY and is watching the page. A later
   * navigation of that frame which finds it still set kept the document (history.pushState, a hash change): only a new
   * document fails a run as a navigation — and any navigation before it is set counts as one.
   */
  runDocument: '__GOTTI_PLAYGROUND_RUN_DOCUMENT__',
} as const;

/** One of the `window` global names in `PLAYGROUND_GLOBALS`. */
export type PlaygroundGlobalKey = (typeof PLAYGROUND_GLOBALS)[keyof typeof PLAYGROUND_GLOBALS];

/**
 * Names of Playwright bindings the runner exposes on the page before navigating there
 * (`page.exposeBinding`, which — per Playwright's own docs — installs on every frame and survives
 * navigations). The runtime calls `publishResult` exactly once, with the finished suite result, the
 * moment it has one: the runner treats the FIRST call as the verdict and ignores any later one, so the
 * read is atomic even if a stray reload lets the suite run twice in the same driven session.
 */
export const PLAYGROUND_BINDINGS = {
  publishResult: '__gottiPlaygroundPublishResult',
} as const;

/** URL query parameters the runner sets on the page it navigates to. */
export const PLAYGROUND_QUERY = {
  runTests: '__gottiPlaygroundTest',
  testLabel: '__gottiPlaygroundTestLabel',
} as const;

/**
 * The first words of the page error the page's entry module script reports when its static module graph fails to load
 * (`PLAYGROUND_ENTRY_ONERROR`), followed by `: ` and the entry's URL. The entry never ran, so READY can never come.
 */
export const PLAYGROUND_MODULE_GRAPH_FAILED = '[gotti][playground] module graph failed to load';

/**
 * The `onerror` handler of a playground page's entry module script. When a fetch anywhere in the entry's static module
 * graph fails (an error status, a refused or failed request), Chromium fires the entry element's `error` event once,
 * raises no page error, and never runs the entry. So the page could never publish READY, and a headless run would wait
 * out its serve budget. `reportError` turns the event into the uncaught page error a run already watches before READY;
 * the runner recognises it by `PLAYGROUND_MODULE_GRAPH_FAILED`. The handler sits on the element, never as a
 * capture-phase `window` listener, which would see every image, stylesheet and classic script error too. It holds no
 * double quote, so it fits any attribute. (A parse or link error, or a failed dynamic import, raises a page error of its
 * own and no element event.)
 */
export const PLAYGROUND_ENTRY_ONERROR = `reportError(new Error('${PLAYGROUND_MODULE_GRAPH_FAILED}: ' + this.src))`;

/** Console lines the runtime prefixes with this are the runner's own progress channel. */
export const PLAYGROUND_TEST_LOG_PREFIX = '[gotti][playground][test]';

/** Console lines a step reports through `ctx.log` start with this, so a runner can tell a step's report from the game's own logging. */
export const PLAYGROUND_STEP_LOG_PREFIX = '[gotti][playground][log]';

/** Which step is running. Emitted by the runtime as a step starts so the runner can tag what follows. */
export interface StepStartMark {
  testId: string;
  stepIndex: number;
  label: string;
}

const STEP_START_HEAD = `${PLAYGROUND_TEST_LOG_PREFIX} ▸ `;

/**
 * One console line announcing a step — the label first, then the same identity as JSON.
 *
 * The line is shown verbatim wherever the console goes (the headless runner's terminal, the
 * dashboard's log panel), so it leads with what a human wants; the trailing JSON is what the runner
 * parses to attribute the lines that follow, and survives any label because it is encoded.
 */
export const formatStepStart = (mark: StepStartMark): string =>
  `${STEP_START_HEAD}${mark.label} ${JSON.stringify({ testId: mark.testId, stepIndex: mark.stepIndex })}`;

/** The machine half of the line: just the identity — the label is the human half in front of it. */
type StepStartPayload = Pick<StepStartMark, 'testId' | 'stepIndex'>;

const isStepStartPayload = (value: unknown): value is StepStartPayload =>
  typeof value === 'object' &&
  value !== null &&
  typeof (value as StepStartPayload).testId === 'string' &&
  Number.isInteger((value as StepStartPayload).stepIndex);

/** The mark carried by a console line, or `null` for every line that is not a complete mark. */
export const parseStepStart = (line: string): StepStartMark | null => {
  if (!line.startsWith(STEP_START_HEAD)) {
    return null;
  }
  // The label is free text and may itself contain braces, so try each `{` from the right until one
  // yields the whole trailing payload; whatever precedes it is the label.
  for (let start = line.lastIndexOf('{'); start > STEP_START_HEAD.length - 1; start = line.lastIndexOf('{', start - 1)) {
    try {
      const parsed: unknown = JSON.parse(line.slice(start));
      if (isStepStartPayload(parsed)) {
        return {
          testId: parsed.testId,
          stepIndex: parsed.stepIndex,
          label: line.slice(STEP_START_HEAD.length, start).trimEnd(),
        };
      }
    } catch {
      /* not the payload's opening brace — keep looking to its left */
    }
  }
  return null;
};

/**
 * One console line closing a sequential step: its outcome and wall cost. The runner prints it after the
 * step's assertions settle. It is a runner line, not a mark: it never moves step attribution.
 */
export const formatStepDone = (done: { label: string; index: number; total: number; durationMs: number; passed: boolean }): string =>
  `${PLAYGROUND_TEST_LOG_PREFIX} ${done.passed ? '✓' : '✗'} step ${done.index + 1}/${done.total} ${done.label} (${Math.round(done.durationMs)}ms)`;

/** Read a playground global from any scope (`window` in the page, `globalThis` elsewhere). */
export const readPlaygroundGlobal = (scope: object, key: PlaygroundGlobalKey): unknown =>
  (scope as Record<string, unknown>)[key];

/** Publish a playground global. */
export const writePlaygroundGlobal = (scope: object, key: PlaygroundGlobalKey, value: unknown): void => {
  (scope as Record<string, unknown>)[key] = value;
};
